import { Checkbox, Combobox } from '@/components'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { Module, ModuleOptionsValue } from '@/modules/companies/consts'
import { ExportFormat, ExportOption } from '@/modules/exports'
import type { ExportPeriod } from '@/modules/exports/components/export-form'
import {
  ExportCard,
  ExportField,
  ExportFormatField,
  ExportMethodField,
  ExportPeriodField,
  ExportUsersField,
} from '@/modules/exports/components/export-form'
import { useMassTimesheetExportForm } from '@/modules/timesheets/hooks'
import { useModuleConfig } from '@/utils/hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { endOfDay, startOfDay } from 'date-fns'
import { useEffect } from 'react'

const DOWNLOAD_EXCLUDED_FORMATS = [
  ExportFormat.EXCEL,
  ExportFormat.POWEROFFICE,
  ExportFormat.VISMA,
  ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
  ExportFormat.TRIPLETEX,
  ExportFormat.PDF_SALARY,
]

export const ExportTimesheetsCard: React.FC = () => {
  const translations = useTranslate({
    exportAll: 'settings.export-all-timesheets',
    exportType: 'common.type',
    facility: 'common.facility',
    orderByUserType: 'common.sort-by-user-type',
    includeAbsences: 'settings.include-absences',
  })

  const {
    loading,
    form,
    errors,
    submit,
    updateForm,
    handleExport,
    exportTypes,
  } = useMassTimesheetExportForm()

  const { getModuleOptionStatus } = useModuleConfig(Module.TIMESHEETS)
  const projectActive = getModuleOptionStatus(
    ModuleOptionsValue.TIMESHEETS_PROJECTS
  )

  const downloadOnly = form.exportOption === ExportOption.DOWNLOAD

  useEffect(() => {
    if (
      form.exportOption === ExportOption.DOWNLOAD &&
      form.exportFormat === ExportFormat.EXCEL
    ) {
      updateForm({ exportFormat: ExportFormat.PDF })
    }
  }, [form.exportOption, form.exportFormat, updateForm])

  // The export hook sends the stored range as-is, so keep it on whole days.
  function handlePeriodChange({
    dateStart,
    dateEnd,
    ...rest
  }: Partial<ExportPeriod>) {
    updateForm({
      ...rest,
      ...(dateStart && { dateStart: startOfDay(dateStart) }),
      ...(dateEnd && { dateEnd: endOfDay(dateEnd) }),
    })
  }

  return (
    <ExportCard
      title={translations.exportAll}
      loading={loading}
      onSubmit={submit(handleExport)}
    >
      <ExportPeriodField value={form} onChange={handlePeriodChange} />

      <ExportField label={translations.exportType}>
        <Combobox
          mode="single"
          noSearch
          value={form.exportType}
          items={exportTypes}
          onChange={exportType => {
            if (exportType) updateForm({ exportType })
          }}
        />
      </ExportField>

      <ExportMethodField
        value={form.exportOption}
        allowDownload
        onChange={exportOption => updateForm({ exportOption })}
      />

      <ExportFormatField
        value={form.exportFormat}
        exclude={downloadOnly ? DOWNLOAD_EXCLUDED_FORMATS : []}
        disabled={downloadOnly}
        onChange={exportFormat => updateForm({ exportFormat })}
      />

      {projectActive && (
        <ExportField label={translations.facility} wide>
          <FacilityCombobox
            mode="single"
            clearable
            value={form.facility ?? ''}
            onChange={facility => updateForm({ facility: facility || null })}
          />
        </ExportField>
      )}

      <ExportUsersField
        value={form.users ?? []}
        error={errors.users}
        dropdownPageName={DropdownNames.TIME_ENTRIES}
        onChange={users => updateForm({ users })}
      />

      <div className="flex flex-wrap gap-x-6 gap-y-3 md:col-span-2">
        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-stone-700">
          <Checkbox
            size="sm"
            value={form.orderByUserType ?? false}
            onChange={checked =>
              updateForm({ orderByUserType: checked === true })
            }
          />
          {translations.orderByUserType}
        </label>

        <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-stone-700">
          <Checkbox
            size="sm"
            value={form.includeAbsences ?? false}
            onChange={checked =>
              updateForm({ includeAbsences: checked === true })
            }
          />
          {translations.includeAbsences}
        </label>
      </div>
    </ExportCard>
  )
}
